import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Tabs, TabList, Tab, TabPanels, TabPanel } from '@indeed/ifl-components';
import JobInfoHeader, { type JobInfoHeaderProps } from './JobInfoHeader';
import { JobDetailsTabContent } from '../review/JobDetailsTabContent';
import { JobInfoHeaderFieldsFragment } from './__generated__/JobInfoHeaderFragments.generated';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import GettextFactory from '../../../util/GettextFactory';

/** Tab identifiers for telemetry — index-aligned with the rendered tabs. */
const TAB_LABELS = ['application', 'job_details'];

// Reset scroll before paint on the client so a newly selected tab is never briefly shown at the
// previous offset; fall back to useEffect during SSR to avoid the useLayoutEffect warning.
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export interface DraftApplicationTabsProps {
    /**
     * The draft whose job info heads the tabs and whose `clientJobRef` enables the Job details tab.
     * Optional so QuestionsPage (whose draft type isn't narrowed to non-null at the call site) can
     * pass it directly; when absent, JobInfoHeader fetches its own and only the Application tab shows.
     */
    draft?: JobInfoHeaderFieldsFragment;
    /** Content of the "Application" tab (the review preview, or the questions card). */
    children: React.ReactNode;
    /** Forwarded to JobInfoHeader — the delete action (only ReviewPage offers it). */
    onDelete?: JobInfoHeaderProps['onDelete'];
    /** Forwarded to JobInfoHeader — whether a delete is in flight. */
    isDeleting?: boolean;
    /**
     * Controlled active tab index. Provide together with `onActiveIndexChange` for the page to both
     * observe and drive the tab — QuestionsPage does, so its footer can bring the Application tab
     * forward before validating. Omit for internal (uncontrolled) tab state (ReviewPage).
     */
    activeIndex?: number;
    /** Called with the newly selected tab index on every switch (and by the controlled parent). */
    onActiveIndexChange?: (index: number) => void;
    /**
     * Background of the **Application** tab's content surface (everything below the sticky
     * header/TabList, including the empty space down to the footer). Defaults to the page surface;
     * QuestionsPage passes `background.strong` so its inset question card reads as a panel on a gray
     * field. The Job details tab always uses the default surface, so it stays white regardless.
     */
    applicationTabBackgroundColor?: string;
}

/**
 * The tabbed content region shared by ReviewPage and QuestionsPage: a sticky `JobInfoHeader` +
 * `TabList` above an "Application" tab (the page's `children`) and, when the draft carries a
 * `clientJobRef`, a "Job details" tab.
 *
 * Owns only the region the two pages duplicate — the page keeps its own `PageTransition`, `Helmet`,
 * footer, any `FieldValidationProvider`, and its loading/failed branches (whose container rules
 * differ). Centralizes the IAFE-2526 scroll reset, the IAFE-1598 sticky header, and the tab-switch
 * telemetry.
 */
const DraftApplicationTabs: React.FunctionComponent<DraftApplicationTabsProps> = ({
    draft,
    children,
    onDelete,
    isDeleting = false,
    activeIndex,
    onActiveIndexChange,
    applicationTabBackgroundColor = 'background',
}) => {
    const gt = GettextFactory();
    const location = useLocation();
    const { draftId } = useParams<{ draftId: string }>();
    const eventLogger = useEventLogger();
    // Controlled when the parent passes `activeIndex`; otherwise this internal state drives the tabs.
    const [internalTabIndex, setInternalTabIndex] = React.useState(0);
    const currentTabIndex = activeIndex ?? internalTabIndex;
    const setCurrentTabIndex = (index: number) => {
        onActiveIndexChange?.(index);
        setInternalTabIndex(index);
    };

    // `applyableJob` is non-null on `JobInfoHeaderFields`, so only `draft`/`clientJobRef` are optional.
    const clientJobRef = draft?.applyableJob.clientJobRef ?? undefined;

    // The Application and Job details tabs share the same (window) scroll container, so the offset
    // would otherwise carry over when switching. Reset to the top whenever the draft or active tab
    // changes (IAFE-2526). A layout effect resets before paint, so the target tab is never briefly
    // rendered at the previous scroll position.
    useIsomorphicLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [draftId, currentTabIndex]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minBlockSize: '100%',
                maxInlineSize: '450px',
                margin: '0 auto',
                // Only the Application tab takes `applicationTabBackgroundColor`; the Job details tab keeps
                // the default surface so it stays white even when the Application tab is a gray field.
                backgroundColor:
                    currentTabIndex === 0 ? applicationTabBackgroundColor : 'background',
                paddingBlockEnd: 10, // Space for the fixed footer
            }}
        >
            {/* JobInfoHeader and TabList share a sticky wrapper inside <Tabs> so they stay pinned
                together while the tab content scrolls (IAFE-1598). */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Tabs
                    index={currentTabIndex}
                    onChange={(newIndex: number) => {
                        eventLogger.logReviewTabSwitch?.({
                            currentPage: location.pathname,
                            draftId: draftId || '',
                            fromTab: TAB_LABELS[currentTabIndex] ?? 'application',
                            toTab: TAB_LABELS[newIndex] ?? 'application',
                        });
                        setCurrentTabIndex(newIndex);
                    }}
                >
                    <Box
                        sx={{
                            position: 'sticky',
                            insetBlockStart: 0,
                            zIndex: 100,
                            backgroundColor: 'background',
                        }}
                    >
                        <JobInfoHeader
                            fragment={draft}
                            onDelete={onDelete}
                            isDeleting={isDeleting}
                        />
                        <TabList
                            previousButtonLabel={gt.gettext('Previous Tab')}
                            nextButtonLabel={gt.gettext('Next Tab')}
                        >
                            <Tab>{gt.gettext('Application')}</Tab>
                            {clientJobRef && <Tab>{gt.gettext('Job details')}</Tab>}
                        </TabList>
                    </Box>
                    {/* IFL's TabPanel sets `display:none` on the inactive panel, so its content is
                        already out of the focus/a11y tree — no aria-hidden-focus risk, and nothing to
                        lazy-render for the Application panel. Keeping it mounted also avoids
                        regenerating ReviewPage's preview (and re-flashing its skeleton) on every tab
                        switch. Job details is still rendered only while active so its job-description
                        fetch is deferred until the tab is opened. */}
                    <TabPanels>
                        <TabPanel>{children}</TabPanel>
                        {clientJobRef && (
                            <TabPanel>
                                {currentTabIndex === 1 ? (
                                    <JobDetailsTabContent jobKey={clientJobRef} />
                                ) : null}
                            </TabPanel>
                        )}
                    </TabPanels>
                </Tabs>
            </Box>
        </Box>
    );
};

export default DraftApplicationTabs;
